import {
  backendValidationErrorFrom,
  type BackendErrorResponse,
  type BackendValidationError,
} from '../../../shared/lib/backend-errors';
import { acceptLanguage } from '@/shared/lib/accept-language';

export interface UserResetRequest {
  resets: Array<{
    user_email: string;
  }>;
}

export interface UserResetResponse {
  success: boolean;
  error?: string;
  data?: any;
  validationError?: BackendValidationError;
}

export interface PendingReset {
  id: string;
  user_email: string;
  status: string;
  created?: string;
}

export interface PendingResetsResponse {
  success: boolean;
  error?: string;
  data?: {
    resets: PendingReset[];
  };
}

export interface ResetPasswordRequest {
  users: Array<{
    public_key: string;
  }>;
}

export interface ResetPasswordResponse {
  success: boolean;
  error?: string;
  data?: any;
  validationError?: BackendValidationError;
}

export async function requestPasswordReset(userEmail: string): Promise<UserResetResponse> {
  try {
    const requestBody: UserResetRequest = {
      resets: [
        {
          user_email: userEmail
        }
      ]
    };

    // Always use /api proxy - handled by Vite in development and nginx in production
    const response = await fetch('/api/user-reset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': acceptLanguage(),
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorData: BackendErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }
      const validationError = backendValidationErrorFrom(
        errorData,
        `HTTP ${response.status}: ${response.statusText}`,
      );
      return {
        success: false,
        error: validationError.message,
        validationError,
      };
    }

    const responseData = await response.json();

    return {
      success: true,
      data: responseData
    };
  } catch (error) {
    console.error('❌ Password reset request error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Lists pending password resets for a reset session
 * Requires authentication with the reset session (from email magic link)
 */
export async function listPendingResets(
  sessionId: string,
  sessionPrivateKey: any
): Promise<PendingResetsResponse> {
  try {
    const { createResetSessionAuthHeaders } = await import('./reset-session');
    const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey);

    // Always use /api proxy - handled by Vite in development and nginx in production
    const response = await fetch('/api/user-reset?status=pending', {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
        if (errorData.errors && errorData.errors.length > 0) {
          return {
            success: false,
            error: errorData.errors[0].message
          };
        }
        return {
          success: false,
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}`
        };
      } catch (e) {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }
    }

    const responseData = await response.json();
    
    return {
      success: true,
      data: responseData
    };
  } catch (error) {
    console.error('❌ List pending resets error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Resets the user's password by submitting the new public key
 * Requires authentication with the reset session (from email magic link)
 */
export async function resetPassword(
  sessionId: string,
  sessionPrivateKey: any,
  resetId: string,
  publicKeyPem: string
): Promise<ResetPasswordResponse> {
  try {
    const { createResetSessionAuthHeaders } = await import('./reset-session');
    
    const requestBody: ResetPasswordRequest = {
      users: [
        {
          public_key: publicKeyPem
        }
      ]
    };

    const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey, requestBody);

    // Always use /api proxy - handled by Vite in development and nginx in production
    const response = await fetch(`/api/user-reset/${resetId}/user`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorData: BackendErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }
      const validationError = backendValidationErrorFrom(
        errorData,
        `HTTP ${response.status}: ${response.statusText}`,
      );
      return {
        success: false,
        error: validationError.message,
        validationError,
      };
    }

    const responseData = await response.json();

    return {
      success: true,
      data: responseData
    };
  } catch (error) {
    console.error('❌ Reset password error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

